import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { KeyRound } from 'lucide-react'
import { platformAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { t, formatDate, formatDateTime } from '../../i18n'
import { PageHeader, SearchInput, Select, Table, Loading, Empty, Chip, Drawer, roleLabel } from './ui'

const ROLE_TONE = { owner: 'violet', admin: 'blue', manager: 'blue', cashier: 'gray', kitchen: 'gray', waiter: 'gray' }

export default function PlatformUsers() {
  const { user: me } = useAuth()
  const [rows, setRows] = useState(null)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const [resetFor, setResetFor] = useState(null)

  const load = useCallback(() => {
    platformAPI.getUsers({ search: search || undefined, role: role || undefined, status: status || undefined })
      .then(r => setRows(r.data.users))
      .catch(e => { setRows([]); toast.error(t(e.response?.data?.error || 'Failed to load users')) })
  }, [search, role, status])
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id) }, [load])

  const toggle = async u => {
    if (u.is_active && !window.confirm(t('Disable {v0}? They can no longer sign in.', { v0: u.username }))) return
    try {
      await platformAPI.setUserActive(u.id, !u.is_active)
      toast.success(u.is_active ? t('Account disabled') : t('Account enabled'))
      load()
    } catch (e) {
      toast.error(t(e.response?.data?.error || 'Failed to update the user'))
    }
  }

  return (
    <div>
      <PageHeader title={t('Users')} subtitle={t('Every account on the platform: business owners and their staff.')}>
        <div className="flex flex-wrap gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder={t('Search username, name or email')} />
          <Select value={role} onChange={setRole}>
            <option value="">{t('All roles')}</option>
            {['owner', 'admin', 'manager', 'cashier', 'kitchen', 'waiter'].map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </Select>
          <Select value={status} onChange={setStatus}>
            <option value="">{t('All statuses')}</option>
            <option value="active">{t('Active')}</option>
            <option value="disabled">{t('Disabled')}</option>
          </Select>
        </div>
      </PageHeader>

      {!rows ? <Loading /> : !rows.length ? <Empty>{t('No users match.')}</Empty> : (
        <Table head={[t('User'), t('Role'), t('Business'), t('Status'), t('Last sign-in'), t('Joined'), '']}>
          {rows.map(u => {
            const locked = u.is_super_admin || u.id === me?.id
            return (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">{u.full_name || u.username}</p>
                  <p className="text-xs text-slate-500">@{u.username}{u.email ? ` · ${u.email}` : ''}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <Chip tone={ROLE_TONE[u.role]}>{roleLabel(u.role)}</Chip>
                    {u.is_super_admin && <Chip tone="amber">{t('Super admin')}</Chip>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {u.businesses.length ? u.businesses.map(b => (
                    <Link key={b.id} to={`/platform/businesses?open=${b.id}`} className="block text-indigo-600 hover:text-indigo-700">{b.name}</Link>
                  )) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3">{u.is_active ? <Chip tone="green">{t('Active')}</Chip> : <Chip tone="red">{t('Disabled')}</Chip>}</td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{u.last_login_at ? formatDateTime(u.last_login_at) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(u.created_at)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-end">
                  {!locked && (
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setResetFor(u)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100">
                        <KeyRound className="h-3.5 w-3.5" />{t('Reset password')}
                      </button>
                      <button onClick={() => toggle(u)} className={`rounded-md px-2 py-1 text-xs font-medium ring-1 ${u.is_active ? 'text-rose-600 ring-rose-200 hover:bg-rose-50' : 'text-emerald-700 ring-emerald-200 hover:bg-emerald-50'}`}>
                        {u.is_active ? t('Disable') : t('Enable')}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </Table>
      )}

      <ResetPassword user={resetFor} onClose={() => setResetFor(null)} />
    </div>
  )
}

function ResetPassword({ user, onClose }) {
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => setPassword(''), [user])

  const submit = async e => {
    e.preventDefault()
    if (password.length < 8) return toast.error(t('Password must be at least 8 characters'))
    setSaving(true)
    try {
      await platformAPI.resetPassword(user.id, password)
      toast.success(t('Password changed. Share it with {v0} privately.', { v0: user.username }))
      onClose()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to reset the password'))
    } finally { setSaving(false) }
  }

  return (
    <Drawer open={!!user} onClose={onClose} title={t('Reset password')}>
      {user && (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">{t('Set a new password for {v0}. Their current password stops working right away.', { v0: user.username })}</p>
          <input type="text" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('New password (8+ characters)')}
            className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-500" />
          <button disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{t('Save new password')}</button>
        </form>
      )}
    </Drawer>
  )
}
